/*
 * The Vault - sections/gb-vault.liquid.
 *
 * A custom element rather than an inline script so the theme editor can
 * re-render the section (every setting change replaces its HTML) and the flow
 * comes back to life on its own.
 *
 * Signups go to Klaviyo's client subscriptions API with the public company
 * key, the same call the footer signup makes (blocks/email-signup.liquid):
 *   step 1  email + email marketing consent, onto the section's list
 *   step 2  phone + SMS marketing consent, same list. If Klaviyo refuses the
 *           SMS subscription (no SMS sending set up for that country) the
 *           number is still stored on the profile, without consent.
 *
 * Progress is remembered per browser, so someone who already signed up comes
 * back to step 3 rather than a blank email field.
 *
 * Every successful signup is also published to Shopify's customer-events bus
 * as `gb:lead` (source "vault", step "email" or "phone"), the event every email
 * capture on the store publishes. Theme code can't reach the Meta and Google
 * pixels - they run in Shopify's sandbox - so the custom pixel in
 * docs/tracking/lead-pixel.js (Settings > Customer events) subscribes to it and
 * sends Lead / generate_lead, gated on the visitor's consent there.
 *
 * PIN step (0): only while the store is locked with a PIN set, read from
 * window.gbVaultLock (sections/gb-vault-lock.liquid). It comes first; a correct
 * PIN stores its SHA-256 as localStorage gbVaultKey, which the lock accepts,
 * and returns the visitor to the page they were sent here from.
 */
(function () {
  if (customElements.get('gb-vault')) return;

  var KLAVIYO = 'https://a.klaviyo.com/client/';
  var REVISION = '2026-07-15';
  var FALLBACK_COMPANY = 'VjRPHv';
  var STORE_KEY = 'gbVault';
  var SOURCE = 'The Vault';

  /*
   * Calling codes by ISO 3166-1 alpha-2. Same table as the fit quiz
   * (src/quiz/lib/dial-codes.js) - Shopify doesn't expose them. A country
   * missing here is dropped from the picker.
   */
  var TABLE =
    'AC247 AD376 AE971 AG1268 AI1264 AL355 AM374 AO244 AR54 AT43 AU61 AW297 AX358 AZ994 BA387 BB1246 BD880 BE32 ' +
    'BF226 BG359 BH973 BI257 BJ229 BL590 BM1441 BN673 BO591 BQ599 BR55 BS1242 BT975 BW267 BZ501 CA1 CC61 CD243 ' +
    'CF236 CG242 CH41 CI225 CK682 CL56 CM237 CN86 CO57 CR506 CV238 CW599 CX61 CY357 CZ420 DE49 DJ253 DK45 DM1767 ' +
    'DO1809 DZ213 EC593 EE372 EG20 EH212 ER291 ES34 ET251 FI358 FJ679 FK500 FO298 FR33 GA241 GB44 GD1473 GE995 ' +
    'GF594 GG44 GH233 GI350 GL299 GM220 GN224 GP590 GQ240 GR30 GT502 GW245 GY592 HK852 HN504 HR385 HT509 HU36 ' +
    'ID62 IE353 IL972 IM44 IN91 IQ964 IS354 IT39 JE44 JM1876 JO962 JP81 KE254 KG996 KH855 KI686 KM269 KN1869 ' +
    'KR82 KW965 KY1345 KZ7 LA856 LB961 LC1758 LI423 LK94 LR231 LS266 LT370 LU352 LV371 MA212 MC377 MD373 ME382 ' +
    'MF590 MG261 MK389 ML223 MM95 MN976 MO853 MQ596 MR222 MS1664 MT356 MU230 MV960 MW265 MX52 MY60 MZ258 NA264 ' +
    'NC687 NE227 NF672 NG234 NI505 NL31 NO47 NP977 NR674 NU683 NZ64 OM968 PA507 PE51 PF689 PG675 PH63 PK92 PL48 ' +
    'PM508 PS970 PT351 PY595 QA974 RE262 RO40 RS381 RW250 SA966 SB677 SC248 SE46 SG65 SH290 SI386 SJ47 SK421 ' +
    'SL232 SM378 SN221 SR597 ST239 SV503 SX1721 SZ268 TA290 TC1649 TD235 TG228 TH66 TJ992 TK690 TL670 TM993 ' +
    'TN216 TO676 TR90 TT1868 TV688 TW886 TZ255 UA380 UG256 US1 UY598 UZ998 VA39 VC1784 VE58 VG1284 VN84 VU678 ' +
    'WF681 WS685 XK383 YT262 ZA27 ZM260 ZW263';
  var DIAL = {};
  TABLE.split(' ').forEach(function (e) {
    DIAL[e.slice(0, 2)] = e.slice(2);
  });

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function flag(iso) {
    return String.fromCodePoint.apply(
      null,
      iso.toUpperCase().split('').map(function (c) {
        return 0x1f1a5 + c.charCodeAt(0);
      })
    );
  }

  function companyId() {
    var s = document.querySelector('script[src*="klaviyo.js"]');
    var m = s && /company_id=([A-Za-z0-9]+)/.exec(s.src);
    return (m && m[1]) || FALLBACK_COMPANY;
  }

  function post(path, body) {
    return fetch(KLAVIYO + path + '?company_id=' + encodeURIComponent(companyId()), {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + res.status);
    });
  }

  /*
   * National number + calling code -> E.164. Drops a leading trunk 0 (040 123
   * 456 in Slovenia is +386 40 123 456); Italy keeps it, its numbers carry the
   * 0 internationally. Accepts a number typed with its own +code too.
   */
  function toE164(raw, iso) {
    var s = String(raw || '').trim();
    var digits = s.replace(/\D/g, '');
    if (/^\+|^00/.test(s)) {
      if (s.indexOf('00') === 0) digits = digits.slice(2);
    } else {
      if (iso !== 'IT') digits = digits.replace(/^0+/, '');
      digits = (DIAL[iso] || '') + digits;
    }
    return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
  }

  /*
   * The page the lock sent her here from (sections/gb-vault-lock.liquid), e.g.
   * /sl-si/products/elena. localStorage because the PIN usually comes by email
   * and is entered in a new tab, often days later; older than 30 days it's
   * stale and she goes to the home page instead.
   */
  var RETURN_TTL = 30 * 24 * 3600 * 1000;
  function returnPath() {
    var back = null;
    try {
      var saved = JSON.parse(localStorage.getItem('gbVaultReturn'));
      localStorage.removeItem('gbVaultReturn');
      if (saved && saved.path && Date.now() - saved.t < RETURN_TTL) back = saved.path;
    } catch (err) {}
    return back;
  }

  /*
   * PIN unlock counts when it's switched on, or once "Enable PIN automatically
   * at" has passed. Server stamp as a lower bound on the time, as in
   * sections/gb-vault-lock.liquid: edge-cached HTML can only make it stale.
   */
  var T0 = Date.now();
  function nowSec(lock) {
    var c = Date.now() / 1000;
    return lock.serverNow ? Math.max(c, lock.serverNow + (Date.now() - T0) / 1000) : c;
  }
  function pinLive(lock) {
    if (!lock.locked || !lock.pin) return false;
    return !!lock.pinOn || (lock.pinAt > 0 && nowSec(lock) >= lock.pinAt);
  }

  function sha256(text) {
    var bytes = new TextEncoder().encode(text);
    return crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      return Array.prototype.map
        .call(new Uint8Array(buf), function (b) {
          return ('0' + b.toString(16)).slice(-2);
        })
        .join('');
    });
  }

  function load() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function save(state) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode: the flow still works, it just isn't remembered */
    }
  }

  class GbVault extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.editor = this.hasAttribute('data-editor');
      this.state = this.editor ? {} : load();
      this.lock = window.gbVaultLock || {};
      this.pinLocked = pinLive(this.lock);
      this.armPinTimer();

      this.video();
      this.phonePicker();
      this.pinBoxes();

      var forms = this.querySelectorAll('form.gb-vault__step');
      for (var i = 0; i < forms.length; i++) {
        forms[i].addEventListener('submit', this.onSubmit.bind(this));
      }
      var skip = this.querySelector('[data-skip]');
      if (skip) skip.addEventListener('click', this.done.bind(this));

      var gotos = this.querySelectorAll('[data-goto]');
      for (var j = 0; j < gotos.length; j++) {
        gotos[j].addEventListener('click', this.onGoto.bind(this));
      }
      var pinOnly = this.querySelectorAll('[data-pin-only]');
      for (var k = 0; k < pinOnly.length; k++) {
        pinOnly[k].hidden = !(this.pinLocked || this.editor);
      }

      // The inline script after the element has normally done this already,
      // before paint; this covers the theme editor's re-renders.
      if (!this.editor) this.show(this.initialStep(), false);
    }

    /*
     * "Enable PIN automatically at": a visitor already on the vault when the
     * countdown ends gets the PIN step without reloading - unless she is in
     * the middle of typing her email or past step 1, in which case only the
     * "Already have a PIN?" links appear. The interval is the reliable path:
     * background tabs throttle timeouts.
     */
    armPinTimer() {
      var lock = this.lock;
      if (this.editor || this.pinLocked || !lock.locked || !lock.pin || !lock.pinAt) return;
      var self = this;
      var tick = setInterval(function () {
        if (!pinLive(lock)) return;
        clearInterval(tick);
        self.pinLocked = true;
        var pinOnly = self.querySelectorAll('[data-pin-only]');
        for (var k = 0; k < pinOnly.length; k++) pinOnly[k].hidden = false;
        var email = self.querySelector('form[data-step-id="1"] input[name="email"]');
        var busy = email && (email.value || document.activeElement === email);
        if (self.dataset.step === '1' && !busy) self.show(0, false);
      }, 1000);
    }

    /* Same rules as the inline step picker in sections/gb-vault.liquid. */
    initialStep() {
      if (this.pinLocked) return 0;
      if (this.state.done) return 3;
      if (this.state.email) return 2;
      return 1;
    }

    onGoto(e) {
      this.show(Number(e.currentTarget.dataset.goto), true);
    }

    /* Load only the video that fits the viewport; the other is never fetched. */
    video() {
      var v = this.querySelector('.gb-vault__video');
      if (!v) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var mobile = window.matchMedia('(max-width: 749px)').matches;
      var src = mobile ? v.dataset.srcMobile : v.dataset.srcDesktop;
      if (!src) return;
      v.muted = true;
      v.src = src;
      v.addEventListener(
        'playing',
        function () {
          v.classList.add('is-playing');
        },
        { once: true }
      );
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    /* Flag and "+386" shown over a transparent native select. */
    phonePicker() {
      var select = this.querySelector('.gb-vault__prefix-select');
      var label = this.querySelector('.gb-vault__prefix-label');
      if (!select || !label) return;
      for (var i = select.options.length - 1; i >= 0; i--) {
        var o = select.options[i];
        if (!DIAL[o.value]) select.remove(i);
        else o.textContent = flag(o.value) + ' ' + o.textContent + ' (+' + DIAL[o.value] + ')';
      }
      if (this.state.country && DIAL[this.state.country]) select.value = this.state.country;
      var paint = function () {
        label.textContent = select.value ? flag(select.value) + ' +' + DIAL[select.value] : '';
      };
      select.addEventListener('change', paint);
      paint();
    }

    /*
     * One box per PIN character over a single transparent input. Fills the
     * boxes as she types or pastes, and submits once they are all filled.
     */
    pinBoxes() {
      var input = this.querySelector('.gb-vault__pin-input');
      var wrap = this.querySelector('.gb-vault__pin');
      var row = this.querySelector('.gb-vault__pin-boxes');
      if (!input || !row) return;
      var n = this.lock.pinLength || 6;
      var numeric = this.lock.pin ? !!this.lock.pinNumeric : true;

      // The inline step picker may have drawn them already, before paint.
      if (row.children.length !== n) {
        row.textContent = '';
        for (var i = 0; i < n; i++) {
          var box = document.createElement('span');
          box.className = 'gb-vault__pin-box';
          row.appendChild(box);
        }
      }
      var boxes = row.children;

      input.maxLength = n;
      if (numeric) {
        input.inputMode = 'numeric';
        input.pattern = '[0-9]*';
      }

      var paint = function () {
        var v = input.value;
        for (var j = 0; j < boxes.length; j++) {
          boxes[j].textContent = v.charAt(j);
          boxes[j].classList.toggle('is-active', j === Math.min(v.length, n - 1));
        }
      };
      this.paintPin = paint;

      input.addEventListener('input', function () {
        var v = numeric ? input.value.replace(/\D/g, '') : input.value.replace(/\s/g, '').toUpperCase();
        input.value = v.slice(0, n);
        if (wrap) wrap.classList.remove('is-wrong');
        paint();
        if (input.value.length === n) {
          var form = input.form;
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      });
      // Keep the caret at the end: the boxes only make sense filled left to right.
      var toEnd = function () {
        var l = input.value.length;
        try {
          input.setSelectionRange(l, l);
        } catch (e) {}
        paint();
      };
      input.addEventListener('focus', toEnd);
      input.addEventListener('click', toEnd);
      input.addEventListener('keyup', toEnd);
      paint();
    }

    wrongPin(form) {
      var input = form.elements.pin;
      var wrap = form.querySelector('.gb-vault__pin');
      input.value = '';
      if (this.paintPin) this.paintPin();
      if (wrap) {
        wrap.classList.remove('is-wrong');
        void wrap.offsetWidth; // restart the shake
        wrap.classList.add('is-wrong');
      }
      input.focus();
    }

    show(n, focus) {
      var steps = this.querySelectorAll('.gb-vault__step');
      for (var i = 0; i < steps.length; i++) {
        steps[i].hidden = steps[i].dataset.stepId !== String(n);
      }
      this.dataset.step = n;
      if (focus) {
        var h = this.querySelector('[data-step-id="' + n + '"] .gb-vault__heading');
        if (h) h.focus({ preventScroll: true });
      }
    }

    error(form, key) {
      var el = form.querySelector('.gb-vault__error');
      var input = form.querySelector('.gb-vault__input');
      if (input) input.setAttribute('aria-invalid', key ? 'true' : 'false');
      if (!el) return;
      el.textContent = key ? el.dataset[key] || '' : '';
      el.hidden = !key;
    }

    profile(extra) {
      var d = this.dataset;
      var attrs = {
        email: this.state.email,
        properties: { Locale: d.locale || '', Country: d.country || '', Market: d.market || '', 'Vault signup': true },
      };
      for (var k in extra) attrs[k] = extra[k];
      return { data: { type: 'profile', attributes: attrs } };
    }

    subscribe(profile) {
      var list = this.lock.list;
      var body = { data: { type: 'subscription', attributes: { custom_source: SOURCE, profile: profile } } };
      if (list) body.data.relationships = { list: { data: { type: 'list', id: list } } };
      return post('subscriptions/', body);
    }

    onSubmit(e) {
      e.preventDefault();
      var form = e.currentTarget;
      if (form.getAttribute('aria-busy') === 'true') return;
      var step = form.dataset.stepId;
      var task =
        step === '0' ? this.submitPin(form) : step === '1' ? this.submitEmail(form) : this.submitPhone(form);
      if (!task) return;
      form.setAttribute('aria-busy', 'true');
      task
        .then(
          function () {
            this.error(form, null);
          }.bind(this),
          function () {
            this.error(form, 'failed');
          }.bind(this)
        )
        .then(function () {
          form.removeAttribute('aria-busy');
        });
    }

    /*
     * Compared as SHA-256 of the trimmed, upper-cased entry, the same
     * normalisation the lock section applies before Liquid's sha256.
     */
    submitPin(form) {
      var entry = form.elements.pin.value.trim().toUpperCase();
      if (!entry) {
        this.error(form, 'invalid');
        return null;
      }
      var lock = this.lock;
      var editor = this.editor;
      var self = this;
      return sha256(entry).then(
        function (hash) {
          if (!lock.pin || hash !== lock.pin) {
            self.wrongPin(form);
            throw new Error('wrong pin');
          }
          if (editor) return;
          try {
            localStorage.setItem('gbVaultKey', hash);
          } catch (err) {
            /* no storage: the lock can't remember it; nothing more to do here */
          }
          var back = returnPath();
          // Only ever a same-origin path: never follow a stored absolute URL.
          if (!back || back.charAt(0) !== '/' || back.charAt(1) === '/') back = lock.home || '/';
          window.location.replace(back);
          return new Promise(function () {}); // keep the form busy while leaving
        }
      );
    }

    submitEmail(form) {
      var email = form.elements.email.value.trim();
      if (!EMAIL_RE.test(email)) {
        this.error(form, 'invalid');
        return null;
      }
      if (this.editor) return Promise.resolve(this.show(2, true));
      this.state.email = email;
      try {
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push(['identify', { $email: email }]);
      } catch (err) {
        /* onsite JS blocked - the subscription below still goes through */
      }
      return this.subscribe(
        this.profile({ subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } })
      ).then(
        function () {
          save(this.state);
          this.track('email');
          this.show(2, true);
        }.bind(this)
      );
    }

    submitPhone(form) {
      var country = form.elements.country.value;
      var phone = toE164(form.elements.phone.value, country);
      if (!phone) {
        this.error(form, 'invalid');
        return null;
      }
      if (this.editor) return Promise.resolve(this.show(3, true));
      this.state.country = country;
      var done = this.done.bind(this);
      var withPhone = this.profile({ phone_number: phone });
      return this.subscribe(
        this.profile({ phone_number: phone, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } })
      )
        .catch(function () {
          // SMS not enabled for this country: keep the number on the profile anyway.
          return post('profiles/', withPhone);
        })
        .then(
          function () {
            this.track('phone', phone);
            done();
          }.bind(this)
        );
    }

    /*
     * Never lets tracking break the flow: Shopify.analytics can be missing
     * (blocked, preview) and a throw here would strand her on the step.
     */
    track(step, phone) {
      try {
        var d = this.dataset;
        var sent = window.Shopify.analytics.publish('gb:lead', {
          source: 'vault',
          form: 'The Vault',
          step: step,
          email: this.state.email || '',
          phone: phone || '',
          market: d.market || '',
          country: d.country || '',
          locale: d.locale || '',
          event_id: 'vault-' + step + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10),
        });
        if (sent && sent.catch) sent.catch(function () {});
      } catch (e) {}
    }

    done() {
      if (!this.editor) {
        this.state.done = true;
        save(this.state);
      }
      this.show(3, true);
    }
  }

  customElements.define('gb-vault', GbVault);
})();
